
import React, { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from '@/context/AuthContext';
import SpeakersAdmin from '@/components/admin/SpeakersAdmin';
import ScheduleAdmin from '@/components/admin/ScheduleAdmin';
import VenueAdmin from '@/components/admin/VenueAdmin';
import TicketsAdmin from '@/components/admin/TicketsAdmin';
import ContactAdmin from '@/components/admin/ContactAdmin';
import EnquiriesAdmin from '@/components/admin/EnquiriesAdmin';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useToast } from '@/hooks/use-toast';
import PageHeader from '@/components/PageHeader';
import { supabase } from '@/integrations/supabase/client';

const AdminDashboard = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const [activeTab, setActiveTab] = useState<string>("speakers");
  const { toast } = useToast();
  const [conferenceId, setConferenceId] = useState<string | undefined>(undefined);
  const [isOrganizer, setIsOrganizer] = useState<boolean | null>(null);
  const [checkingRole, setCheckingRole] = useState(true);

  useEffect(() => {
    const checkOrganizerAccess = async () => {
      if (!isAuthenticated || !user) {
        setCheckingRole(false);
        return;
      }

      const { data: profileData } = await supabase
        .from('profiles')
        .select('is_organizer')
        .eq('id', user.id)
        .maybeSingle();

      setIsOrganizer(!!profileData?.is_organizer);
      setCheckingRole(false);
    };

    if (!loading) {
      checkOrganizerAccess();
    }
  }, [isAuthenticated, user, loading]);

  if (loading || checkingRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    toast({
      title: "Authentication Required",
      description: "Please login to access the admin dashboard",
      variant: "destructive",
    });
    return <Navigate to="/auth" />;
  }

  if (!isOrganizer) {
    toast({
      title: "Access Denied",
      description: "You must be an organizer to access the admin dashboard",
      variant: "destructive",
    });
    return <Navigate to="/" />;
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-black to-indigo-950">
      <Header />
      <PageHeader 
        title=""
        subtitle=""
        backgroundImage="/lovable-uploads/75e6360c-a56e-48e7-9e3c-5903e26ee0a6.png"
      />
      <main className="flex-grow pt-28 pb-20">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              Admin Dashboard
            </h1>
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-8 bg-white/10">
              <TabsTrigger 
                value="speakers" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Speakers
              </TabsTrigger>
              <TabsTrigger 
                value="schedule" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Schedule
              </TabsTrigger>
              <TabsTrigger 
                value="venue" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Venue
              </TabsTrigger>
              <TabsTrigger 
                value="tickets" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Tickets
              </TabsTrigger>
              <TabsTrigger 
                value="contact" 
                className="text-white data-[state=active]:bg-purple-600"
              >
                Contact
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="speakers" className="mt-0">
              <SpeakersAdmin conferenceId={conferenceId} />
            </TabsContent>
            
            <TabsContent value="schedule" className="mt-0">
              <ScheduleAdmin />
            </TabsContent>

            <TabsContent value="venue" className="mt-0">
              <VenueAdmin conferenceId={conferenceId} />
            </TabsContent>

            <TabsContent value="tickets" className="mt-0">
              <TicketsAdmin conferenceId={conferenceId} />
            </TabsContent>
            
            <TabsContent value="contact" className="mt-0">
              <EnquiriesAdmin />
              <div className="mt-12"><ContactAdmin conferenceId={conferenceId || ""} /></div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminDashboard;
